import { useContext, useState } from 'react';
import { toast } from 'react-toastify';
import { Context } from 'Context/Context';
import { generateSignedurlForDocsApi, removeDocumentApi, updateDocumentsApi } from 'Constants';

// Supporting documents / video statement upload for one case - the same flow
// as the case page (CaseBaseInfo.jsx): ask the server for signed S3 URLs, PUT
// each file, then save the list on the case. onChange() runs after every
// successful upload or removal so the caller can reload the case.
// caseDetails is used for the 10-documents limit.
const MAX_DOC_SIZE = 10 * 1024 * 1024; // 10MB
const MAX_VIDEO_SIZE = 150 * 1024 * 1024; // 150MB
const MAX_DOCUMENTS = 10;

export default function useCaseDocuments(caseId, caseDetails, onChange = () => {}) {
  const { makeApiCallFunc } = useContext(Context);
  const [uploading, setUploading] = useState(null); // "supportingDocuments" | "videoDocument" | null

  const validateFiles = (files, name) => {
    const fileArr = Array.from(files);
    if (!fileArr.length) return false;
    if (name === 'videoDocument') {
      if (fileArr.length > 1) { toast.error('Please choose one video only.'); return false; }
      if (fileArr[0].size > MAX_VIDEO_SIZE) { toast.error('Video size too large (max 150MB)'); return false; }
    } else {
      const left = MAX_DOCUMENTS - (caseDetails?.supportingDocuments?.length || 0);
      if (fileArr.length > left) { toast.error(`You can only upload up to ${MAX_DOCUMENTS} documents in total.`); return false; }
      const big = fileArr.find((f) => f.size > MAX_DOC_SIZE);
      if (big) { toast.error(`File too large: ${big.name} (max 10MB)`); return false; }
    }
    return true;
  };

  const uploadToSignedUrls = async (urls, files, name) => {
    const fileArr = Array.from(files);
    try {
      await Promise.all(urls.map(async (url, i) => {
        const res = await fetch(url.location, { method: 'PUT', headers: { 'Content-Type': fileArr[i].type }, body: fileArr[i] });
        if (!res.ok) throw new Error(`Upload failed: ${fileArr[i].name}`);
      }));
      // only after ALL uploads succeed
      makeApiCallFunc(updateDocumentsApi + '/' + caseId, { [name]: urls, documentTypeName: name },
        () => { setUploading(null); toast.success(name === 'videoDocument' ? 'Video uploaded' : 'Documents uploaded'); onChange(); },
        (err) => { console.log(err); setUploading(null); toast.error('Could not save your files. Please try again.'); },
        null, true);
    } catch (err) {
      console.error('Upload error:', err);
      setUploading(null);
      toast.error('Upload failed. Please try again.');
    }
  };

  const uploadDocuments = (e, name) => {
    // copy now: clearing the input below (e.target.value = '') empties the
    // live FileList, and the upload runs later in the callback
    const files = Array.from(e.target.files || []);
    if (!validateFiles(files, name)) { e.target.value = ''; return; }
    setUploading(name);
    const list = Array.from(files).map((f) => ({ fileName: f.name, fileType: f.type }));
    makeApiCallFunc(generateSignedurlForDocsApi, { files: list, caseId },
      ({ data }) => uploadToSignedUrls(data?.url || [], files, name),
      (err) => { console.log(err); setUploading(null); toast.error('Upload failed. Please try again.'); },
      null, true);
    e.target.value = ''; // so the same file can be picked again
  };

  const removeDoc = (fileName, updatedPostData, callback = () => {}) => {
    makeApiCallFunc(removeDocumentApi, { fileName, updatedPostData, id: caseId },
      () => { onChange(); callback(); toast.success(fileName + ' removed'); },
      (err) => { console.log(err); callback(); },
      null, true);
  };

  return { uploadDocuments, removeDoc, uploading };
}
